import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import { useAuth } from '../auth/AuthProvider';
import { Button, Card, Chip, ErrorRetry, useToast, type ChipTone } from '../ui';

/**
 * صندوق استعلام‌ها — the inbox for quotation requests captured by the public
 * website (POST /api/public/inquiries). Opening a new inquiry marks it read;
 * «تبدیل به پیشنهاد» matches-or-creates the customer and hands a prefill to
 * the deal form, which links the resulting deal back (status → converted).
 */

export interface Inquiry {
  id: number;
  created_at: string;
  locale: 'fa' | 'ar' | 'en';
  company: string;
  contact: string;
  phone: string;
  email: string;
  market: 'IR' | 'IQ' | 'other';
  product_name: string;
  product_code: string;
  qty: number;
  delivery: string;
  notes: string;
  status: 'new' | 'read' | 'converted' | 'archived';
  deal_id: number | null;
}

export interface DealPrefill {
  inquiryId: number;
  customerId: number | null;
  productCode: string;
  productName: string;
  qty: number;
  notes: string;
}

interface CustomerRow {
  id: number;
  name: string;
  phone: string;
  email: string;
}

const STATUS_FA: Record<Inquiry['status'], string> = {
  new: 'جدید',
  read: 'خوانده‌شده',
  converted: 'تبدیل‌شده',
  archived: 'بایگانی',
};
const STATUS_TONE: Record<Inquiry['status'], ChipTone> = {
  new: 'warn',
  read: 'info',
  converted: 'success',
  archived: 'neutral',
};
const MARKET_FA: Record<Inquiry['market'], string> = {
  IR: 'ایران',
  IQ: 'عراق',
  other: 'سایر',
};
const LOCALE_FA: Record<Inquiry['locale'], string> = {
  fa: 'فارسی',
  ar: 'عربی',
  en: 'انگلیسی',
};

/** Persian/Arabic-Indic digits → ASCII, plus separator noise, for phone matching. */
const normalizePhone = (s: string) =>
  s
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/[\s\-()+]/g, '');

const faDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString('fa-IR', { dateStyle: 'medium' }) : '—';

export function InquiriesScreen({ onConvert }: { onConvert: (p: DealPrefill) => void }) {
  const { user } = useAuth();
  const toast = useToast();
  const canManage = user?.role === 'Admin' || user?.role === 'Sales';
  const { data, status, error, reload } = useApiResource<Inquiry[]>('/api/inquiries');
  // Local copy so `patch` can optimistically update a single row; re-synced on every (re)load.
  const [rows, setRows] = useState<Inquiry[]>([]);
  useEffect(() => {
    if (data) setRows(data);
  }, [data]);
  const [filter, setFilter] = useState<'all' | Inquiry['status']>('all');
  const [openId, setOpenId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );
  const open = rows.find((r) => r.id === openId) ?? null;
  const newCount = rows.filter((r) => r.status === 'new').length;

  const patch = useCallback(async (id: number, status: Inquiry['status'], dealId?: number) => {
    const r = await api<Inquiry>(`/api/inquiries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dealId ? { status, deal_id: dealId } : { status }),
    });
    if (r.ok) setRows((prev) => prev.map((x) => (x.id === id ? (r.body as Inquiry) : x)));
    return r.ok;
  }, []);

  function openInquiry(inq: Inquiry) {
    setOpenId(inq.id);
    // Opening a fresh inquiry marks it read — but only for roles that may write.
    if (inq.status === 'new' && canManage) void patch(inq.id, 'read');
  }

  /** Match the sender to an existing customer (phone first, then exact name) or create one. */
  async function resolveCustomer(inq: Inquiry): Promise<number | null> {
    const r = await api<CustomerRow[]>('/api/customers');
    if (r.ok && Array.isArray(r.body)) {
      const phone = normalizePhone(inq.phone);
      const match =
        r.body.find((c) => c.phone && normalizePhone(c.phone) === phone) ??
        r.body.find((c) => c.name.trim() === inq.company.trim());
      if (match) return match.id;
    }
    const created = await api<{ id: number }>('/api/customers', {
      method: 'POST',
      body: JSON.stringify({
        name: inq.company,
        phone: inq.phone,
        email: inq.email,
        notes: `ایجادشده از استعلام سایت #${inq.id} — تماس: ${inq.contact}`,
      }),
    });
    if (created.ok) return (created.body as { id: number }).id;
    return null;
  }

  async function convert(inq: Inquiry) {
    setBusy(true);
    const customerId = await resolveCustomer(inq);
    setBusy(false);
    if (customerId === null) {
      toast('ساخت مشتری ممکن نشد — دسترسی یا اتصال را بررسی کنید.', 'error');
      return;
    }
    const parts = [
      `استعلام سایت #${inq.id} (${faDate(inq.created_at)})`,
      `تماس: ${inq.contact || '—'} — ${inq.phone}`,
      inq.delivery && `تحویل درخواستی: ${inq.delivery}`,
      inq.market !== 'IR' && `بازار: ${MARKET_FA[inq.market]}`,
      inq.notes && `توضیحات مشتری: ${inq.notes}`,
    ].filter(Boolean);
    onConvert({
      inquiryId: inq.id,
      customerId,
      productCode: inq.product_code,
      productName: inq.product_name,
      qty: inq.qty,
      notes: parts.join('\n'),
    });
  }

  const FILTERS: ['all' | Inquiry['status'], string][] = [
    ['all', 'همه'],
    ['new', `جدید${newCount ? ` (${newCount})` : ''}`],
    ['read', 'خوانده‌شده'],
    ['converted', 'تبدیل‌شده'],
    ['archived', 'بایگانی'],
  ];

  return (
    <>
      {open && (
        <Card title={`استعلام #${open.id} — ${open.company}`}>
          <div className="inqdetail">
            <dl>
              <dt>تاریخ ثبت</dt>
              <dd>{faDate(open.created_at)}</dd>
              <dt>تماس‌گیرنده</dt>
              <dd>{open.contact || '—'}</dd>
              <dt>تلفن</dt>
              <dd dir="ltr">{open.phone}</dd>
              <dt>ایمیل</dt>
              <dd dir="ltr">{open.email || '—'}</dd>
              <dt>بازار / زبان</dt>
              <dd>
                {MARKET_FA[open.market]} · {LOCALE_FA[open.locale]}
              </dd>
              <dt>محصول</dt>
              <dd>
                {open.product_name}
                {open.product_code && <span className="muted"> ({open.product_code})</span>}
              </dd>
              <dt>تعداد</dt>
              <dd>{open.qty.toLocaleString('fa-IR')}</dd>
              <dt>تحویل درخواستی</dt>
              <dd>{open.delivery || '—'}</dd>
              {open.notes && (
                <>
                  <dt>توضیحات</dt>
                  <dd className="prewrap">{open.notes}</dd>
                </>
              )}
              {open.deal_id && (
                <>
                  <dt>پیشنهاد مرتبط</dt>
                  <dd>#{open.deal_id}</dd>
                </>
              )}
            </dl>
            <div className="row" style={{ marginTop: 12 }}>
              {canManage && open.status !== 'converted' && (
                <Button loading={busy} onClick={() => void convert(open)}>
                  تبدیل به پیشنهاد
                </Button>
              )}
              {canManage && open.status !== 'archived' && (
                <Button variant="secondary" onClick={() => void patch(open.id, 'archived')}>
                  بایگانی
                </Button>
              )}
              {canManage && open.status === 'archived' && (
                <Button variant="secondary" onClick={() => void patch(open.id, 'read')}>
                  بازگردانی
                </Button>
              )}
              <Button variant="ghost" onClick={() => setOpenId(null)}>
                بستن
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card title={`صندوق استعلام‌های وب‌سایت (${rows.length})`}>
        <div className="row" style={{ marginBottom: 10 }}>
          {FILTERS.map(([key, label]) => (
            <Button
              key={key}
              variant={filter === key ? 'primary' : 'ghost'}
              onClick={() => setFilter(key)}
            >
              {label}
            </Button>
          ))}
        </div>
        {status === 'error' ? (
          <ErrorRetry error={error} onRetry={() => reload()} />
        ) : status === 'loading' ? (
          <p className="muted">در حال بارگذاری…</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>تاریخ</th>
                <th>شرکت / تماس</th>
                <th>محصول</th>
                <th>تعداد</th>
                <th>بازار</th>
                <th>وضعیت</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td className="empty" colSpan={7}>
                    استعلامی در این نما نیست — فرم «استعلام قیمت» سایت این‌جا می‌نشیند.
                  </td>
                </tr>
              ) : (
                visible.map((r) => (
                  <tr key={r.id} style={r.status === 'new' ? { fontWeight: 600 } : undefined}>
                    <td>{faDate(r.created_at)}</td>
                    <td>
                      {r.company}
                      <div className="muted">
                        {r.contact || '—'} · <span dir="ltr">{r.phone}</span>
                      </div>
                    </td>
                    <td>{r.product_name}</td>
                    <td>{r.qty.toLocaleString('fa-IR')}</td>
                    <td>{MARKET_FA[r.market]}</td>
                    <td>
                      <Chip tone={STATUS_TONE[r.status]}>{STATUS_FA[r.status]}</Chip>
                    </td>
                    <td>
                      <Button variant="ghost" onClick={() => openInquiry(r)}>
                        باز کردن
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
